import { initializeApp, getApps, getApp } from 'firebase/app'
import {
  initializeAuth,
  GoogleAuthProvider,
  browserLocalPersistence,
  indexedDBLocalPersistence,
  browserPopupRedirectResolver,
  connectAuthEmulator,
  onAuthStateChanged,
  onIdTokenChanged,
} from 'firebase/auth'
import { getFirestore } from 'firebase/firestore'

// Validate required environment variables
const requiredEnvVars = {
  VITE_FIREBASE_API_KEY: import.meta.env.VITE_FIREBASE_API_KEY,
  VITE_FIREBASE_AUTH_DOMAIN: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  VITE_FIREBASE_PROJECT_ID: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  VITE_FIREBASE_STORAGE_BUCKET: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  VITE_FIREBASE_MESSAGING_SENDER_ID: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  VITE_FIREBASE_APP_ID: import.meta.env.VITE_FIREBASE_APP_ID,
}

const missingVars = Object.entries(requiredEnvVars)
  .filter(([, value]) => !value)
  .map(([key]) => key)

if (missingVars.length > 0) {
  throw new Error(
    `Missing required Firebase environment variables: ${missingVars.join(', ')}. ` +
      'Please ensure these are set in your .env file with VITE_ prefix and restart the Vite dev server.'
  )
}

const firebaseConfig = {
  apiKey: requiredEnvVars.VITE_FIREBASE_API_KEY,
  authDomain: requiredEnvVars.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: requiredEnvVars.VITE_FIREBASE_PROJECT_ID,
  storageBucket: requiredEnvVars.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: requiredEnvVars.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: requiredEnvVars.VITE_FIREBASE_APP_ID,
}

const app = !getApps().length ? initializeApp(firebaseConfig) : getApp()

// localStorage first matches where the session is stored, so a new tab never moves or deletes it
export const auth = initializeAuth(app, {
  persistence: [browserLocalPersistence, indexedDBLocalPersistence],
  popupRedirectResolver: browserPopupRedirectResolver,
})
export const db = getFirestore(app)

/**
 * Configure auth instance for multi-tenancy
 * @param {string} tenantId - The Google Identity Platform tenant ID
 */
export const setAuthTenant = (tenantId) => {
  if (tenantId) {
    auth.tenantId = tenantId
  } else {
    auth.tenantId = null
  }
}

export const googleProvider = new GoogleAuthProvider()
googleProvider.addScope('email')
googleProvider.addScope('profile')

googleProvider.setCustomParameters({
  prompt: 'select_account', // Always show account selection
})

// Connect to auth emulator in development if configured
if (import.meta.env.MODE === 'development' && import.meta.env.VITE_USE_AUTH_EMULATOR === 'true') {
  const emulatorUrl = import.meta.env.VITE_AUTH_EMULATOR_URL || 'http://localhost:9099'
  console.log('Connecting to Firebase Auth Emulator at:', emulatorUrl)
  connectAuthEmulator(auth, emulatorUrl, { disableWarnings: true })
}

export { onAuthStateChanged, onIdTokenChanged }
export default app
